/**
 * Service for obtaining Access Button tokens from the Evo platform.
 *
 * When running inside the Access Evo iframe, the `EvoIframeApi` class
 * from `@access-evo/evo-api` communicates with the parent window via
 * `postMessage` to retrieve an Access Button token.
 *
 * If the Evo API is unavailable (e.g. running outside the Evo shell
 * during local development), the service falls back gracefully to
 * `null` so callers can decide how to handle unauthenticated requests.
 */

import { EvoIframeApi } from '@access-evo/evo-api';

/**
 * Retrieve an Access Button token string.
 *
 * Creates an `EvoIframeApi` instance and calls its `getButtonToken()`
 * method, which uses the `postMessage` bridge to request the token
 * from the parent Evo host.
 *
 * @returns The bearer token, or `null` if unavailable.
 */
export async function getButtonToken(): Promise<string | null> {
  const api = new EvoIframeApi();
  try {
    const response = await api.getButtonToken();

    if (response.status === 200 && 'access_token' in response.body) {
      const token = response.body.access_token;
      if (token) {
        return token;
      }
    }

    return null;
  } catch {
    return null;
  } finally {
    api.destroy();
  }
}
